import { getCourseFileUrl } from './courseContentMainHelpers';

// Files a browser can show itself. Word, Excel and PowerPoint files cannot be shown in a page, so for those the
// student gets Download only (an instructor who wants them to be viewable adds a link or uploads a PDF).
const VIEWABLE_EXTENSIONS = ['pdf', 'html', 'htm'];

export const fileExtension = (name) => {
  const text = String(name || '').split('?')[0].split('#')[0];
  return text.includes('.') ? text.split('.').pop().toLowerCase() : '';
};

export const canViewInBrowser = (fileName) => VIEWABLE_EXTENSIONS.includes(fileExtension(fileName));

const hostOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (error) { return ''; }
};

// The saved file's address with ?download=1 (and its original name), so the browser saves it instead of opening it.
export const courseFileDownloadUrl = (fileUrl, fileName) => {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl; // a file on another site: the link's own download behavior applies
  const name = fileName ? `&name=${encodeURIComponent(fileName)}` : '';
  return `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}download=1${name}`;
};

/**
 * What a student can do with a syllabus entry, reading or slide, from what the instructor provided:
 *
 *   a link (or links), no file  ->  View (each link)
 *   a file, no link             ->  View + Download for a PDF or web page; Download only for an Office file
 *   a link and a file           ->  View (the link) + Download (the file)
 *
 * `links` are already-safe http(s) addresses: [{ url, label? }]. `file` is { path, name }.
 */
export const resolveContentActions = ({ links = [], file = null }) => {
  const actions = [];
  const filePath = file && file.path ? file.path : '';
  const fileUrl = getCourseFileUrl(filePath);
  const fileName = (file && file.name) || (filePath ? filePath.split('/').pop() : '');

  links.forEach((link) => {
    actions.push({ kind: 'view', source: 'link', href: link.url, label: link.label || 'View', host: hostOf(link.url) });
  });

  if (fileUrl) {
    if (links.length === 0 && canViewInBrowser(fileName)) {
      actions.push({ kind: 'view', source: 'file', href: fileUrl, label: 'View', host: '' });
    }
    actions.push({ kind: 'download', source: 'file', href: courseFileDownloadUrl(fileUrl, fileName), label: 'Download', fileName: fileName || 'download' });
  }
  return actions;
};

// Sites where a link only works for students if its sharing setting lets them in.
const SHARING_HOSTS = /(^|\.)(docs\.google\.com|drive\.google\.com|slides\.google\.com|onedrive\.live\.com|1drv\.ms|sharepoint\.com|dropbox\.com|box\.com|notion\.so)$/i;

/**
 * What students will get from what an instructor filled in, in words for the instructor (used by the content form
 * and by Quick Add). Returns { buttons, note, warning, sharing, problem }.
 * `link` is the normalized link, '' for none, or null for one that is not a valid web address.
 * `hasOlderLinks` is true for an older reading that still has extra links next to the main one.
 *
 *   link only            -> View
 *   file only            -> View + Download (PDF or web page); Download only (Word, Excel, PowerPoint)
 *   link and file        -> View (the link) + Download (the file)
 */
export const studentPreview = ({ link, fileName, hasOlderLinks = false }) => {
  const hasLink = (typeof link === 'string' && link !== '') || hasOlderLinks;
  const hasFile = Boolean(fileName);
  if (link === null) return { buttons: [], problem: 'That link is not a valid web address. It should look like https://example.com' };
  if (!hasLink && !hasFile) return { buttons: [], problem: 'Nothing for students to open yet. Add a link or upload a file.' };
  const sharing = typeof link === 'string' && SHARING_HOSTS.test(hostOf(link))
    ? 'This link only works for students if its sharing setting lets them open it.'
    : '';
  if (hasLink && hasFile) return { buttons: ['View', 'Download'], note: 'View opens the link. Download saves the file.', sharing };
  if (hasLink) return { buttons: ['View'], note: 'View opens the link.', sharing };
  if (canViewInBrowser(fileName)) return { buttons: ['View', 'Download'], note: 'View opens the file in the browser. Download saves it.' };
  return {
    buttons: ['Download'],
    warning: 'Students can only download this file. Word, Excel and PowerPoint files cannot be shown in the browser. To let them view it too, upload a PDF or add a link.',
  };
};
